import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { CalendarOff, Clock, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  listHolidays,
  addHolidays,
  deleteHoliday,
  setHolidayRecurring,
  listCollabDays,
  saveCollabDay,
  deleteCollabDay,
  listSpilloverBlocks,
  importSpilloverBlocks,
  deleteSpilloverBlock,
} from "@/lib/holidays.functions";
import { usHolidays, ruleKeyFor } from "@/lib/holidays";
import { getMe } from "@/lib/core.functions";
import { PageHeader, EmptyState } from "@/components/AppShell";
import { formatDate, formatTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/holidays")({
  head: () => ({
    meta: [
      { title: "Holidays | Leadership Edge Scheduling" },
      { name: "description", content: "Block US holidays so the builder shifts course runs around them." },
      { property: "og:title", content: "Holidays | Leadership Edge Scheduling" },
      { property: "og:description", content: "Dates the schedule builder keeps clear of class sessions." },
    ],
  }),
  component: HolidaysPage,
});

interface SpillRow {
  title: string;
  block_date: string;
  start_time: string;
  end_time: string;
}

function padTime(value: string): string | null {
  // Accepts "14:00", "2:00 PM", "2 pm", "2:00:00 PM". AM/PM must be honored,
  // otherwise "2:00 PM" would be saved as 2:00 in the morning.
  const match = value.trim().match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap])?\.?\s*m?\.?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = match[2] ?? "00";
  const meridiem = match[3]?.toLowerCase();
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (meridiem === "p" && hour !== 12) hour += 12;
    if (meridiem === "a" && hour === 12) hour = 0;
  } else if (!match[2]) {
    return null;
  }
  if (hour > 23 || Number(minute) > 59) return null;
  return `${String(hour).padStart(2, "0")}:${minute}`;
}

function plusThirty(time: string): string {
  const parts = time.split(":");
  const total = Number(parts[0] ?? 0) * 60 + Number(parts[1] ?? 0) + 30;
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function toIsoDate(value: string): string | null {
  const raw = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parts = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!parts) return null;
  return `${parts[3]}-${(parts[1] ?? "").padStart(2, "0")}-${(parts[2] ?? "").padStart(2, "0")}`;
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let quoted = false;
  for (const char of line) {
    if (char === '"') quoted = !quoted;
    else if (char === "," && !quoted) {
      out.push(current);
      current = "";
    } else current += char;
  }
  out.push(current);
  return out.map((v) => v.trim().replace(/^"|"$/g, ""));
}

/** Reads a spillover file with Date, Title, Scheduled Start and Scheduled End columns. */
function parseSpilloverCsv(text: string): SpillRow[] {
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length < 2) return [];
  const header = splitCsvLine(lines[0] ?? "").map((h) => h.toLowerCase());
  const col = (needle: string) => header.findIndex((h) => h.includes(needle));
  const dateAt = col("date");
  const titleAt = col("title");
  const startAt = header.findIndex((h) => h.includes("start"));
  const endAt = header.findIndex((h) => h.includes("end"));
  if (dateAt < 0 || startAt < 0) return [];
  const rows: SpillRow[] = [];
  for (const line of lines.slice(1)) {
    const cells = splitCsvLine(line);
    const date = toIsoDate(cells[dateAt] ?? "");
    const start = padTime(cells[startAt] ?? "");
    if (!date || !start) continue;
    const end = endAt >= 0 ? padTime(cells[endAt] ?? "") : null;
    rows.push({
      title: (titleAt >= 0 ? cells[titleAt] : "") || "Carried-over session",
      block_date: date,
      start_time: start,
      end_time: end && end > start ? end : plusThirty(start),
    });
  }
  return rows;
}

function HolidaysPage() {
  const queryClient = useQueryClient();
  const fetchMe = useServerFn(getMe);
  const fetchHolidays = useServerFn(listHolidays);
  const add = useServerFn(addHolidays);
  const remove = useServerFn(deleteHoliday);
  const setRecurring = useServerFn(setHolidayRecurring);
  const fetchCollab = useServerFn(listCollabDays);
  const saveCollab = useServerFn(saveCollabDay);
  const removeCollab = useServerFn(deleteCollabDay);
  const fetchSpill = useServerFn(listSpilloverBlocks);
  const importSpill = useServerFn(importSpilloverBlocks);
  const removeSpill = useServerFn(deleteSpilloverBlock);



  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const { data, isPending } = useQuery({ queryKey: ["holidays"], queryFn: () => fetchHolidays() });

  const isAdmin = me?.isAdmin ?? false;
  const holidays = data ?? [];

  const thisYear = new Date().getFullYear();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(thisYear);
  const [picked, setPicked] = useState<string[]>([]);
  const [customName, setCustomName] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [customRecurring, setCustomRecurring] = useState(true);

  const { data: collabData, isPending: collabPending } = useQuery({
    queryKey: ["collab-days"],
    queryFn: () => fetchCollab(),
  });
  const collabDays = collabData ?? [];
  const emptyCollab = { id: null as string | null, name: "Collab Day", collab_date: "", start_time: "14:00", end_time: "16:00", note: "" };
  const [collabOpen, setCollabOpen] = useState(false);
  const [collabForm, setCollabForm] = useState(emptyCollab);

  const { data: spillData, isPending: spillPending } = useQuery({
    queryKey: ["spillover-blocks"],
    queryFn: () => fetchSpill(),
  });
  const spillBlocks = spillData ?? [];

  const invalidateSpill = () => {
    queryClient.invalidateQueries({ queryKey: ["spillover-blocks"] });
    queryClient.invalidateQueries({ queryKey: ["period"] });
  };

  const spillImportMutation = useMutation({
    mutationFn: (rows: SpillRow[]) => importSpill({ data: { rows, replace: true } }),
    onSuccess: (result) => {
      toast.success(`${result.added} carried-over session${result.added === 1 ? "" : "s"} loaded.`);
      invalidateSpill();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const spillDeleteMutation = useMutation({
    mutationFn: (id: string) => removeSpill({ data: { id } }),
    onSuccess: () => {
      toast.success("Carried-over session removed.");
      invalidateSpill();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  async function handleSpillFile(file: File | null) {
    if (!file) return;
    const rows = parseSpilloverCsv(await file.text());
    if (rows.length === 0) {
      toast.error("No sessions found. The file needs Date, Title and Scheduled Start columns.");
      return;
    }
    spillImportMutation.mutate(rows);
  }



  const options = useMemo(() => usHolidays(year), [year]);
  const existing = useMemo(
    () => new Set(holidays.map((h) => (h.recurring ? `rule:${h.rule_key ?? ruleKeyFor(h.name)}` : `${h.holiday_date}|${h.name}`))),
    [holidays],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["holidays"] });
    queryClient.invalidateQueries({ queryKey: ["period"] });
  };

  const addMutation = useMutation({
    mutationFn: (rows: { name: string; holiday_date: string; note: string | null; recurring: boolean; rule_key: string | null }[]) => add({ data: { holidays: rows } }),
    onSuccess: () => {
      toast.success("Holidays added.");
      setOpen(false);
      setPicked([]);
      setCustomName("");
      setCustomDate("");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const recurringMutation = useMutation({
    mutationFn: (vars: { id: string; recurring: boolean }) => setRecurring({ data: vars }),
    onSuccess: () => invalidate(),
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Holiday removed.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const invalidateCollab = () => {
    queryClient.invalidateQueries({ queryKey: ["collab-days"] });
    queryClient.invalidateQueries({ queryKey: ["period"] });
  };

  const collabSaveMutation = useMutation({
    mutationFn: (vars: typeof emptyCollab) =>
      saveCollab({
        data: {
          id: vars.id,
          name: vars.name.trim() || "Collab Day",
          collab_date: vars.collab_date,
          start_time: vars.start_time,
          end_time: vars.end_time,
          note: vars.note.trim() ? vars.note.trim() : null,
        },
      }),
    onSuccess: () => {
      toast.success("Collab Day saved.");
      setCollabOpen(false);
      setCollabForm(emptyCollab);
      invalidateCollab();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const collabDeleteMutation = useMutation({
    mutationFn: (id: string) => removeCollab({ data: { id } }),
    onSuccess: () => {
      toast.success("Collab Day removed.");
      invalidateCollab();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submitCollab() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(collabForm.collab_date)) {
      toast.error("Pick a date for the Collab Day.");
      return;
    }
    if (collabForm.end_time <= collabForm.start_time) {
      toast.error("The end time must be after the start time.");
      return;
    }
    collabSaveMutation.mutate(collabForm);
  }




  function submit() {
    const rows: { name: string; holiday_date: string; note: string | null; recurring: boolean; rule_key: string | null }[] = options
      .filter((o) => picked.includes(`${o.date}|${o.name}`))
      .map((o) => ({ name: o.name, holiday_date: o.date, note: null, recurring: true, rule_key: ruleKeyFor(o.name) }));
    if (customName.trim() && /^\d{4}-\d{2}-\d{2}$/.test(customDate)) {
      rows.push({ name: customName.trim(), holiday_date: customDate, note: null, recurring: customRecurring, rule_key: null });
    }
    if (rows.length === 0) {
      toast.error("Pick at least one holiday.");
      return;
    }
    addMutation.mutate(rows);
  }

  return (
    <>
      <PageHeader
        eyebrow="Blackout dates"
        title="Holidays"
        description="Holidays repeat every year until you remove them. No class session is scheduled on a holiday. A course never loses a week; the builder shifts the whole run so every weekly session lands on a clear date."
        actions={
          isAdmin ? (
            <Button variant="brass" onClick={() => setOpen(true)}>
              <Plus /> Add holiday
            </Button>
          ) : null
        }
      />

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : holidays.length === 0 ? (
        <EmptyState title="No holidays yet" body="Add US holidays so the schedule builder keeps those days clear." />
      ) : (
        <ul className="space-y-2">
          {holidays.map((holiday) => (
            <li key={holiday.id} className="panel flex items-center justify-between gap-4 px-4 py-3">
              <div className="flex items-center gap-3">
                <CalendarOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <div>
                  <p className="text-sm font-medium text-foreground">{holiday.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {formatDate(holiday.holiday_date)}
                    {holiday.recurring ? " · repeats every year" : ""}
                  </p>
                </div>
              </div>
              {isAdmin ? (
                <div className="flex items-center gap-2">
                  <Label htmlFor={`rec-${holiday.id}`} className="cursor-pointer text-xs font-normal text-muted-foreground">
                    Yearly
                  </Label>
                  <Checkbox
                    id={`rec-${holiday.id}`}
                    checked={holiday.recurring ?? true}
                    onCheckedChange={(value) => recurringMutation.mutate({ id: holiday.id, recurring: value === true })}
                  />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:bg-destructive/10"
                  onClick={() => deleteMutation.mutate(holiday.id)}
                >
                  <Trash2 /> Remove
                </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      <section className="mt-10 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Collab Days</h2>
            <p className="text-sm text-muted-foreground">
              A Collab Day blocks one time segment on a single date. No class session is scheduled inside that window.
            </p>
          </div>
          {isAdmin ? (
            <Button
              variant="brass"
              onClick={() => {
                setCollabForm(emptyCollab);
                setCollabOpen(true);
              }}
            >
              <Plus /> Add Collab Day
            </Button>
          ) : null}
        </div>

        {collabPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : collabDays.length === 0 ? (
          <EmptyState title="No Collab Days yet" body="Add a Collab Day to keep a specific block of time free for it." />
        ) : (
          <ul className="space-y-2">
            {collabDays.map((day) => (
              <li key={day.id} className="panel flex items-center justify-between gap-4 px-4 py-3">
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{day.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {formatDate(day.collab_date)} · {formatTime(day.start_time.slice(0, 5))} to{" "}
                      {formatTime(day.end_time.slice(0, 5))} blocked
                    </p>
                    {day.note ? <p className="text-xs text-muted-foreground">{day.note}</p> : null}
                  </div>
                </div>
                {isAdmin ? (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setCollabForm({
                          id: day.id,
                          name: day.name,
                          collab_date: day.collab_date,
                          start_time: day.start_time.slice(0, 5),
                          end_time: day.end_time.slice(0, 5),
                          note: day.note ?? "",
                        });
                        setCollabOpen(true);
                      }}
                    >
                      <Pencil /> Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => collabDeleteMutation.mutate(day.id)}
                    >
                      <Trash2 /> Remove
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10 space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Spillover sessions</h2>
            <p className="text-sm text-muted-foreground">
              Sessions carried over from an earlier quarter. Their date and time stays taken, so no new session is
              scheduled in that slot. Upload a file with Date, Title, Scheduled Start and Scheduled End columns to
              replace this list.
            </p>
          </div>
          {isAdmin ? (
            <div className="space-y-1.5">
              <Label htmlFor="spill-upload" className="text-xs text-muted-foreground">
                Upload spillover file (.csv)
              </Label>
              <Input
                id="spill-upload"
                type="file"
                accept=".csv,text/csv"
                disabled={spillImportMutation.isPending}
                onChange={(e) => {
                  void handleSpillFile(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
            </div>
          ) : null}
        </div>

        {spillPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : spillBlocks.length === 0 ? (
          <EmptyState
            title="No carried-over sessions"
            body="Upload a spillover file to keep those dates and times free."
          />
        ) : (
          <ul className="space-y-2">
            {spillBlocks.map((block) => (
              <li key={block.id} className="panel flex items-center justify-between gap-4 px-4 py-3">
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{block.title}</p>
                    <p className="font-mono text-xs text-muted-foreground">
                      {formatDate(block.block_date)} · {formatTime(block.start_time.slice(0, 5))} to{" "}
                      {formatTime(block.end_time.slice(0, 5))} taken
                    </p>
                    {block.note ? <p className="text-xs text-muted-foreground">{block.note}</p> : null}
                  </div>
                </div>
                {isAdmin ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => spillDeleteMutation.mutate(block.id)}
                  >
                    <Trash2 /> Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>



      <Dialog open={collabOpen} onOpenChange={setCollabOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{collabForm.id ? "Edit Collab Day" : "Add Collab Day"}</DialogTitle>
            <DialogDescription>Sessions are never scheduled inside the blocked time segment.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="collab-name">Name</Label>
              <Input
                id="collab-name"
                value={collabForm.name}
                onChange={(e) => setCollabForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Collab Day"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="collab-date">Date</Label>
              <Input
                id="collab-date"
                type="date"
                value={collabForm.collab_date}
                onChange={(e) => setCollabForm((prev) => ({ ...prev, collab_date: e.target.value }))}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="collab-start">Blocked from</Label>
                <Input
                  id="collab-start"
                  type="time"
                  value={collabForm.start_time}
                  onChange={(e) => setCollabForm((prev) => ({ ...prev, start_time: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="collab-end">Blocked until</Label>
                <Input
                  id="collab-end"
                  type="time"
                  value={collabForm.end_time}
                  onChange={(e) => setCollabForm((prev) => ({ ...prev, end_time: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="collab-note">Reason (optional)</Label>
              <Input
                id="collab-note"
                value={collabForm.note}
                onChange={(e) => setCollabForm((prev) => ({ ...prev, note: e.target.value }))}
                placeholder="Team collaboration session"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="soft" onClick={() => setCollabOpen(false)}>
              Cancel
            </Button>
            <Button variant="brass" disabled={collabSaveMutation.isPending} onClick={submitCollab}>
              {collabForm.id ? "Save changes" : "Add Collab Day"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add holidays</DialogTitle>
            <DialogDescription>Selected holidays repeat every year, so moving dates like Thanksgiving stay correct.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="holiday-year">Year</Label>
              <Input
                id="holiday-year"
                type="number"
                min={thisYear - 1}
                max={thisYear + 5}
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
              />
            </div>

            <ul className="space-y-2">
              {options.map((option) => {
                const key = `${option.date}|${option.name}`;
                const already = existing.has(`rule:${ruleKeyFor(option.name)}`) || existing.has(key);
                return (
                  <li key={key} className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5">
                    <Checkbox
                      id={`opt-${key}`}
                      disabled={already}
                      checked={already || picked.includes(key)}
                      onCheckedChange={(value) =>
                        setPicked((prev) => (value === true ? [...prev, key] : prev.filter((k) => k !== key)))
                      }
                    />
                    <Label htmlFor={`opt-${key}`} className="cursor-pointer font-normal">
                      {option.name}
                      <span className="ml-2 text-xs text-muted-foreground">{formatDate(option.date)}</span>
                      {already ? <span className="ml-2 text-xs text-muted-foreground">Already added</span> : null}
                    </Label>
                  </li>
                );
              })}
            </ul>

            <div className="grid gap-3 border-t border-border pt-4 sm:grid-cols-[2fr_1fr]">
              <div className="space-y-1.5">
                <Label htmlFor="custom-name">Other holiday (optional)</Label>
                <Input
                  id="custom-name"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="Company closure"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="custom-date">Date</Label>
                <Input id="custom-date" type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} />
              </div>
              <div className="flex items-center gap-2 sm:col-span-2">
                <Checkbox
                  id="custom-recurring"
                  checked={customRecurring}
                  onCheckedChange={(value) => setCustomRecurring(value === true)}
                />
                <Label htmlFor="custom-recurring" className="cursor-pointer font-normal">
                  Repeat this date every year
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="soft" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="brass" disabled={addMutation.isPending} onClick={submit}>
              Add selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
